import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../api/client';
import { Loading, ApiError } from '../components/ui/Loading';
import { formatDate } from '../utils/helpers';

// ─── Utilitaire : formatage durée ────────────────────────────────────────────
const fmtDuration = (min) => {
  if (!min && min !== 0) return '—';
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h${min % 60 > 0 ? ` ${min % 60}min` : ''}`;
};

// ─── Rapport performance QR codes ────────────────────────────────────────────
export const Courses = () => {
  const today30 = new Date(); today30.setDate(today30.getDate() - 30);
  const [startDate, setStartDate] = useState(today30.toISOString().slice(0, 10));
  const [endDate,   setEndDate]   = useState(new Date().toISOString().slice(0, 10));
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getQRPerformanceReport({ start_date: startDate, end_date: endDate });
      setData(res.data);
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const successRate = data?.summary
    ? {
        qr1: data.summary.total_orders > 0 ? ((data.summary.qr1_scanned / data.summary.total_orders) * 100).toFixed(1) : '0.0',
        qr3: data.summary.total_orders > 0 ? ((data.summary.qr3_scanned / data.summary.total_orders) * 100).toFixed(1) : '0.0',
        qr4: data.summary.total_orders > 0 ? ((data.summary.qr4_scanned / data.summary.total_orders) * 100).toFixed(1) : '0.0',
      }
    : null;

  return (
    <div>
      {/* En-tête */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 24, marginBottom: 6 }}>
          📲 Rapport QR Codes
        </div>
        <div style={{ color: 'var(--text2)', fontSize: 13 }}>
          Analyse de la performance des scans QR1 / QR3 / QR4 par période
        </div>
      </div>

      {/* Filtres dates */}
      <div className="filter-bar" style={{ marginBottom: 20 }}>
        <div className="form-row" style={{ gap: 12, flexWrap: 'wrap', margin: 0 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Date début</label>
            <input
              type="date"
              className="form-input"
              value={startDate}
              max={endDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Date fin</label>
            <input
              type="date"
              className="form-input"
              value={endDate}
              min={startDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={e => setEndDate(e.target.value)}
            />
          </div>
          <button
            className="btn btn-primary btn-sm"
            style={{ alignSelf: 'flex-end' }}
            onClick={fetchReport}
            disabled={loading}
          >
            Appliquer
          </button>
        </div>

        {/* Raccourcis période */}
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          {[
            { label: '7j',  days: 7  },
            { label: '30j', days: 30 },
            { label: '90j', days: 90 },
          ].map(s => {
            const d = new Date();
            d.setDate(d.getDate() - s.days);
            return (
              <button
                key={s.label}
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setStartDate(d.toISOString().slice(0, 10));
                  setEndDate(new Date().toISOString().slice(0, 10));
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? <Loading text="Chargement du rapport..." />
        : error ? <ApiError message={error} onRetry={fetchReport} />
        : !data ? null
        : (
          <>
            {/* KPIs */}
            <div className="stats-grid" style={{ marginBottom: 20 }}>
              {[
                { icon: '📦', label: 'Total commandes', val: data.summary.total_orders,  color: 'var(--text)'   },
                { icon: '🔵', label: 'QR1 — taux scan', val: `${successRate.qr1}%`,      color: 'var(--blue)'   },
                { icon: '🟠', label: 'QR3 — taux scan', val: `${successRate.qr3}%`,      color: 'var(--orange)' },
                { icon: '🟢', label: 'QR4 — taux scan', val: `${successRate.qr4}%`,      color: 'var(--green)'  },
              ].map((s, i) => (
                <div key={i} className="stat-card">
                  <div className="stat-card-accent" style={{ background: s.color }} />
                  <div className="stat-icon" style={{ background: 'var(--bg3)', fontSize: 18 }}>{s.icon}</div>
                  <div className="stat-value" style={{ color: s.color }}>{s.val}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Temps moyens */}
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header">
                <div className="card-title">⏱️ Temps moyens par étape</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { label: 'Création commande → QR1 scanné',     val: data.summary.avg_time_to_qr1, max: 60, color: 'var(--blue)'   },
                  { label: 'Réception laverie → QR3 scanné',      val: data.summary.avg_time_to_qr3, max: 30, color: 'var(--orange)' },
                  { label: 'QR3 scanné → QR4 (livraison client)', val: data.summary.avg_time_to_qr4, max: 60, color: 'var(--green)'  },
                ].map((row, i) => {
                  const mins = row.val ? Math.round(row.val) : null;
                  const pct  = mins ? Math.min(100, Math.round((mins / row.max) * 100)) : 0;
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: 'var(--text2)' }}>{row.label}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: row.color }}>
                          {mins !== null ? fmtDuration(mins) : '—'}
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${pct}%`, background: row.color, transition: 'width 0.5s' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tableau jour par jour */}
            {data.daily?.length > 0 && (
              <div className="card" style={{ padding: 0 }}>
                <div className="card-header" style={{ padding: '16px 20px 12px' }}>
                  <div className="card-title">📅 Évolution quotidienne</div>
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>{data.daily.length} jours</span>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Commandes</th>
                        <th style={{ color: 'var(--blue)'   }}>QR1</th>
                        <th style={{ color: 'var(--orange)' }}>QR3</th>
                        <th style={{ color: 'var(--green)'  }}>QR4</th>
                        <th>Taux global</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.daily].reverse().map((row, i) => {
                        const globalRate = row.orders > 0
                          ? (((row.qr1 + row.qr3 + row.qr4) / (row.orders * 3)) * 100).toFixed(0)
                          : 0;
                        return (
                          <tr key={i}>
                            <td className="td-main">{formatDate(row.date)}</td>
                            <td>{row.orders}</td>
                            {[
                              { val: row.qr1, color: 'var(--blue)'   },
                              { val: row.qr3, color: 'var(--orange)' },
                              { val: row.qr4, color: 'var(--green)'  },
                            ].map((cell, j) => (
                              <td key={j}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ color: cell.color, fontWeight: 600 }}>{cell.val}</span>
                                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                                    {row.orders > 0 ? `${Math.round((cell.val / row.orders) * 100)}%` : '—'}
                                  </span>
                                </div>
                              </td>
                            ))}
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div className="progress-bar" style={{ flex: 1, height: 6 }}>
                                  <div className="progress-fill" style={{
                                    width: `${globalRate}%`,
                                    background: globalRate >= 80
                                      ? 'var(--green)'
                                      : globalRate >= 50
                                        ? 'var(--orange)'
                                        : 'var(--red)',
                                  }} />
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 600, minWidth: 32 }}>{globalRate}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )
      }
    </div>
  );
};
